import { useState } from 'react'
import { useSearchParams } from 'react-router'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useEngineAgentsList, useEngineAgent, useCreateEngineAgent, useDuplicateEngineAgent, useDeleteEngineAgent, type AgentListItem } from '@/entities/engineAgent'
import { useCampaignsList } from '@/entities/campaign'
import type { TypeEngineAgent } from '@intone/shared/types'
import { AgentForm } from '../agent-page/ui/AgentForm'
import { EntityList } from '@/widgets/entityList'

export function AgentsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedAgentId = searchParams.get('agent_id')
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null)

  const { data: agentsList = [], isLoading: listLoading } = useEngineAgentsList()
  const { data: agentData, isLoading: agentLoading } = useEngineAgent(
    selectedAgentId || undefined
  )
  const { data: campaigns = [] } = useCampaignsList()
  const createAgent = useCreateEngineAgent()
  const duplicateAgent = useDuplicateEngineAgent()
  const deleteAgent = useDeleteEngineAgent()

  const handleDuplicate = async (agent: AgentListItem) => {
    try {
      const result = await duplicateAgent.mutateAsync(agent.alt_key)
      toast.success('Agent duplicated')
      setSearchParams({ agent_id: result.alt_key })
    } catch {
      toast.error('Failed to duplicate agent')
    }
  }

  const handleDelete = async (agent: AgentListItem) => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this agent? This action cannot be undone.'
    )
    if (!confirmed) return

    try {
      await deleteAgent.mutateAsync(agent.alt_key)
      toast.success('Agent deleted')
      if (selectedAgentId === agent.alt_key) {
        const newParams = new URLSearchParams(searchParams)
        newParams.delete('agent_id')
        setSearchParams(newParams)
      }
    } catch {
      toast.error('Failed to delete agent')
    }
  }

  const handleCreateAgent = async () => {
    if (!selectedCampaignId) return
    try {
      const result = await createAgent.mutateAsync(selectedCampaignId)
      toast.success('Agent created')
      setSearchParams({ agent_id: result.alt_key })
      setShowCreateDialog(false)
      setSelectedCampaignId(null)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create agent')
    }
  }

  const handleSelectAgent = (agent: AgentListItem) => {
    setSearchParams({ agent_id: agent.alt_key })
  }

  const createButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={() => setShowCreateDialog(true)}
      className="w-full"
    >
      <Plus className="w-3 h-3 mr-2" />
      Add Agent
    </Button>
  )

  return (
    <div className="w-full h-full p-6 overflow-hidden flex flex-col">
      <div className="mb-4 flex-shrink-0">
        <h1 className="text-heading font-bold text-gray-900 mb-1">Agents</h1>
        <p className="text-detail text-gray-600">
          Configure LLM, voice, STT, and engine settings for each agent
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
        {/* Left Panel - Agent List */}
        <EntityList<AgentListItem>
          items={agentsList}
          selectedId={selectedAgentId ?? undefined}
          getId={(a) => a.alt_key}
          onSelect={handleSelectAgent}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
          renderPrimary={(a) => a.name || 'Unnamed'}
          renderSecondary={(a) => (
            <>
              <div className="text-detail text-gray-500 truncate mt-0.5">{a.campaign_name}</div>
              <div className="text-detail text-gray-400 truncate">{a.llm_name}</div>
            </>
          )}
          searchPlaceholder="Search agents..."
          searchFilter={(a, q) =>
            a.name.toLowerCase().includes(q) ||
            a.campaign_name.toLowerCase().includes(q) ||
            a.voice_name.toLowerCase().includes(q) ||
            a.llm_name.toLowerCase().includes(q)
          }
          isLoading={listLoading}
          createButton={createButton}
        />

        {/* Right Panel - Agent Editor */}
        <div className="flex-1 overflow-y-auto">
          {agentLoading ? (
            <div className="bg-white rounded-lg border border-gray-200 p-6 text-body text-gray-500">
              Loading agent configuration...
            </div>
          ) : selectedAgentId && agentData ? (
            <AgentForm key={selectedAgentId} agentId={selectedAgentId} agent={agentData as TypeEngineAgent} hideHeader />
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
              <p className="text-body">Select an agent from the list to edit its configuration</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Agent Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        setShowCreateDialog(open)
        if (!open) setSelectedCampaignId(null)
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Create New Agent</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-detail font-medium text-muted-foreground">Campaign</label>
              <Select
                value={selectedCampaignId?.toString() ?? ''}
                onValueChange={(v) => setSelectedCampaignId(parseInt(v, 10))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a campaign" />
                </SelectTrigger>
                <SelectContent>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id.toString()}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleCreateAgent}
              disabled={!selectedCampaignId || createAgent.isPending}
              className="w-full"
            >
              {createAgent.isPending ? 'Creating...' : 'Create Agent'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}